import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet'
import { Formik } from 'formik'
import { useEffectOnce } from 'react-use'
import { isEmpty, pathOr } from 'ramda'
import * as yup from 'yup'
import { useSelector } from 'react-redux'

import {
  CleaveField,
  Divider,
  FormField,
  FormLoader,
  Heading,
  InfoBox,
  Radio,
  RadioGroup,
  Svg,
  TextField,
  TextInput,
} from '../../../../components'
import FormErrors from '../../../molecules/FormErrors/FormErrors'
import { apiStateSelectors } from '../../../../../modules/api/apiState'
import { userApiEvents } from '../../../../../modules/api/user'
import styles from './OnboardingVerificationForm.module.scss'
import { TextFieldTypes } from 'components/atoms/TextField/text-field.types'
import { identityNumberValidation } from 'utils/yup'
import { $TSFixMe } from 'types/ts-migrate'
import { format, isValid } from 'date-fns'
import PhoneField from 'views/components/atoms/PhoneField/PhoneField'
import { transformTwoDigitYearToFour } from 'utils/date'
import { extractDialCode } from 'utils/formUtils'
import { createPhoneFieldChangeHandler, hasNumberBeyondDialCode } from '../../FormGroups/utils'
import SvgShieldUser from 'views/components/atoms/Icons/Security/ShieldUser'
import SvgLocked from 'views/components/atoms/Icons/Security/Locked'
import Info from 'views/components/atoms/Svgs/Info'

type OnboardingFormValues = {
  firstName: string
  lastName: string
  idNumber: string
  passport: string
  dateOfBirth: string
  cellNumber: string
  emailAddress: string
  emailVerified: boolean
  role: string
}

type OtpError = {
  message?: string
}

interface OnboardingVerificationFormProps {
  handleSubmit: (values: OnboardingFormValues) => void
  initialValues: OnboardingFormValues
  otpErrors?: OtpError[]
  isLoading?: boolean
  isSubmitting?: boolean
  getOtp: (cellNumber: string) => void
  validateOtp?: (otp: string, values: OnboardingFormValues) => void
  resendEmailVerification: () => void
  getCurrentUserDetails: () => void
}

const LockedLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className={styles['locked-label']}>
    {children}
    <SvgLocked viewBox="0 0 32 32" width={16} height={16} />
  </span>
)

interface CellNumberFieldProps {
  cellNumber: string
  setFieldValue: (field: string, value: any) => void
  error: string | false | undefined
}

const CellNumberField: React.FC<CellNumberFieldProps> = ({ cellNumber, setFieldValue, error }) => {
  const cellNumberReplaced = cellNumber.replace(/\s/g, '')
  const [cellDialCode, setCellDialCode] = useState(() => extractDialCode(cellNumber))

  useEffect(() => {
    const newCellDialCode = extractDialCode(cellNumber)
    if (cellNumber && newCellDialCode !== cellDialCode) {
      setCellDialCode(newCellDialCode)
    }
  }, [cellNumber])

  return (
    <FormField>
      <TextField
        inputComponent={
          <PhoneField
            defaultCountry="za"
            value={cellNumber.includes(cellDialCode) ? cellNumber : [cellDialCode, cellNumberReplaced].join(' ')}
            disabled
            onChange={createPhoneFieldChangeHandler(
              'cellNumber',
              setFieldValue,
              setCellDialCode,
              hasNumberBeyondDialCode,
            )}
          />
        }
        label={<LockedLabel>Contact number</LockedLabel>}
        error={error}
      />
    </FormField>
  )
}

const validationSchema = yup.object().shape({
  firstName: yup
    .string()
    .required('required.')
    .matches(/^[^0-9]*$/, {
      message: 'must be alphabetic.',
    }),
  lastName: yup
    .string()
    .required('required.')
    .matches(/^[^0-9]*$/, {
      message: 'must be alphabetic.',
    }),
  dateOfBirth: yup.string().when('passport', {
    is: (val: $TSFixMe) => val && val.length > 0,
    then: yup.string().required('required.'),
  }),
})

const validationSchemaWithSaIdNumber = validationSchema.shape({
  idNumber: identityNumberValidation,
})

const validationSchemaWithPassportNumber = validationSchema.shape({
  passport: yup.string(),
})

const OnboardingVerificationForm: React.FC<OnboardingVerificationFormProps> = ({
  handleSubmit,
  initialValues,
  otpErrors,
  isLoading,
  isSubmitting,
  getOtp,
  resendEmailVerification,
  getCurrentUserDetails,
}) => {
  // Effects

  useEffectOnce(() => {
    getCurrentUserDetails()
  })

  const getGeneralFormErrorsByEvent = useSelector(apiStateSelectors.getGeneralFormErrorsByEvent)
  const getFormFieldErrorsByEvent = useSelector(apiStateSelectors.getFormFieldErrorsByEvent)

  const getCurrentIdentificationType = initialValues.passport !== '' ? 'passportNumber' : 'saIdNumber'
  const [stateIdentificationType, setStateIdentificationType] = useState(getCurrentIdentificationType)

  // An ID number is permanent once saved: it locks the field and the identification
  // type toggle. A passport stays editable, and can be swapped for an ID number,
  // which locks on save.
  const isIdNumberLocked = initialValues.idNumber !== ''

  const getValidationSchema = () => {
    return stateIdentificationType === 'saIdNumber'
      ? validationSchemaWithSaIdNumber
      : validationSchemaWithPassportNumber
  }

  const switchUserRoleTitles = (role: string) => {
    switch (role) {
      case 'Owner':
        return 'Account owner'
      case 'TeamMember':
        return 'Team member'
      case 'ReadOnlyMember':
        return 'Read only member'
    }
  }

  const switchUserRoleDescriptions = (role: string) => {
    switch (role) {
      case 'Owner':
        return 'Full access, including payment approvals, settings, and user management.'
      case 'TeamMember':
        return 'Day-to-day access for managing leases, invoices and contacts.'
      case 'ReadOnlyMember':
        return 'View and download access only. Cannot make changes.'
    }
  }

  return (
    <>
      <Helmet>
        <title>reOS | Settings | Profile</title>
      </Helmet>
      <Formik
        validationSchema={getValidationSchema}
        initialValues={initialValues}
        enableReinitialize={true}
        onSubmit={handleSubmit}
      >
        {({ values, touched, handleChange, setFieldValue, handleBlur, errors, handleSubmit }) => {
          const submitEvent = userApiEvents.saveDetails_request(values)
          const generalErrors = getGeneralFormErrorsByEvent(submitEvent)
          const fieldErrors = getFormFieldErrorsByEvent(submitEvent)
          const hasApiErrors = generalErrors.length > 0 || !isEmpty(fieldErrors)

          const getFieldValue = (name: any) => pathOr('', [name], values)

          const yy = getFieldValue('idNumber').substring(0, 2)
          const mm = getFieldValue('idNumber').substring(2, 4)
          const dd = getFieldValue('idNumber').substring(4, 6)

          const fourDigitYear = transformTwoDigitYearToFour(yy)
          const dob = new Date(parseInt(fourDigitYear ?? ''), parseInt(mm) - 1, parseInt(dd))
          const dateOfBirthFormatted = isValid(dob) ? format(dob, 'yyyy-MM-dd') : ''

          const formLoaderState = isLoading
            ? 'loading'
            : isSubmitting
            ? 'submitting'
            : !isEmpty(errors) || hasApiErrors
            ? 'error'
            : undefined

          return (
            <FormLoader onSubmit={handleSubmit} state={formLoaderState} buttonProps={{ children: 'Update' }}>
              <FormErrors errors={generalErrors} />
              <div className="rbn--row">
                <div className="rbn--col-md-8">
                  <InfoBox type="info" className={styles['info-box']}>
                    <SvgShieldUser />
                    <div className={styles['info-box__content']}>
                      <header>
                        <h4>{switchUserRoleTitles(values.role)}</h4>
                        <span className={styles['info-box__pill']}>Your role</span>
                      </header>
                      <p>{switchUserRoleDescriptions(values.role)}</p>
                    </div>
                  </InfoBox>
                </div>
              </div>
              <Divider />
              <Heading as="h3" size="a5" spacing="small">
                Personal details
              </Heading>
              <div className="rbn--row">
                <div className="rbn--col-md-4">
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.text}
                          name="firstName"
                          value={values.firstName}
                          placeholder="e.g. Bruce"
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      label="First name"
                      error={touched.firstName && errors.firstName}
                    />
                  </FormField>
                </div>
                <div className="rbn--col-md-4">
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.text}
                          name="lastName"
                          value={values.lastName}
                          placeholder="e.g. Wayne"
                          onChange={handleChange}
                          onBlur={handleBlur}
                        />
                      }
                      label="Last name"
                      error={touched.lastName && errors.lastName}
                    />
                  </FormField>
                </div>
                <div className="rbn--col-md-8">
                  <RadioGroup
                    name="identificationType"
                    className="radio-group"
                    stackRadios={false}
                    label="Identification type"
                    defaultSelected={stateIdentificationType}
                    onChange={(e: $TSFixMe) => {
                      setStateIdentificationType(e.target.value)
                      if (e.target.value === 'passportNumber') {
                        setFieldValue('idNumber', '')
                        setFieldValue('dateOfBirth', '')
                      } else {
                        setFieldValue('passport', '')
                        setFieldValue('dateOfBirth', '')
                      }
                    }}
                  >
                    <Radio name="saIdNumber" value="saIdNumber" label="South African ID number" allowUncheck={false} />
                    <Radio name="passportNumber" value="passportNumber" label="Passport number" allowUncheck={false} />
                  </RadioGroup>
                </div>
                <div className="rbn--col-md-4">
                  <FormField>
                    <TextField
                      inputComponent={
                        stateIdentificationType === 'saIdNumber' ? (
                          <TextInput
                            type={TextFieldTypes.text}
                            name="idNumber"
                            value={values.idNumber}
                            placeholder="e.g. 8605030000080"
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        ) : (
                          <TextInput
                            type={TextFieldTypes.text}
                            name="passport"
                            placeholder="e.g. AA123456"
                            value={getFieldValue('passport')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                          />
                        )
                      }
                      label={stateIdentificationType === 'saIdNumber' ? 'ID number' : 'Passport number'}
                      error={
                        stateIdentificationType === 'saIdNumber'
                          ? touched.idNumber && errors.idNumber
                          : touched.passport && errors.passport
                      }
                    />
                  </FormField>
                </div>
                <div className="rbn--col-md-4">
                  <FormField>
                    <CleaveField
                      name="dateOfBirth"
                      type="date"
                      id="dateOfBirth"
                      label="Date of birth"
                      placeholder="e.g 1986-05-03"
                      value={
                        stateIdentificationType === 'saIdNumber' && getFieldValue('idNumber').length > 6
                          ? dateOfBirthFormatted
                          : values.dateOfBirth
                      }
                      onChange={handleChange}
                      error={errors.dateOfBirth ? String(errors.dateOfBirth) : undefined}
                      disabled={stateIdentificationType === 'saIdNumber'}
                    />
                  </FormField>
                </div>
                {/* {stateIdentificationType === 'passportNumber' && (
                  <div className="rbn--col-md-8">
                    <p className={styles['identification-hint']}>
                      <Info width={20} height={20} />
                      Switching to a South African ID number is permanent. Once saved, it can't be changed.
                    </p>
                  </div>
                )} */}
              </div>
              <Divider />
              <Heading as="h3" size="a5" spacing="small">
                Contact details
              </Heading>

              <div className="rbn--row">
                <div className="rbn--col-md-4">
                  <FormField>
                    <TextField
                      inputComponent={
                        <TextInput
                          type={TextFieldTypes.email}
                          disabled
                          name="emailAddress"
                          value={values.emailAddress}
                          placeholder="e.g. bruce.wayne@gmail.com"
                        />
                      }
                      label={<LockedLabel>Email address</LockedLabel>}
                      trailingIcon={values.emailVerified && <Svg name="tick" width="14px" />}
                    />
                  </FormField>
                </div>
                <div className="rbn--col-md-4">
                  <CellNumberField
                    cellNumber={values.cellNumber}
                    setFieldValue={setFieldValue}
                    error={touched.cellNumber && errors.cellNumber}
                  />
                </div>
              </div>

              {!values.emailVerified && (
                <p>
                  We sent you an email to this address, please click the button in the email to complete the
                  verification process. Haven't received it yet? &nbsp;
                  <button
                    className={styles.resend}
                    onClick={e => {
                      e.preventDefault()
                      resendEmailVerification()
                    }}
                  >
                    Resend verification.
                  </button>
                </p>
              )}

              <Divider />
            </FormLoader>
          )
        }}
      </Formik>
    </>
  )
}

export default OnboardingVerificationForm
